import * as React from "react";
import Chart from 'react-apexcharts';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';

import dayjs, { Dayjs } from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { Bars, SectionCard, StatCard, EmptyState, currency, number } from './ui';
import { CHART_COLORS, TRUE_COLORS } from '../theme';
import { PAYMENT_LABELS, brandLabel } from './salePayment';
import { Profit, ProfitByProduct, ProfitSummary } from './Profit';
import { CashList, CashMovement, CashSummary } from './CashMovements';

type Preset = 'today' | 'week' | 'month' | 'year' | 'custom';
type Range = { start: Dayjs; end: Dayjs };

const PRESETS: { value: Preset; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Año' },
  { value: 'custom', label: 'Personalizado' },
];

const presetRange = (preset: Preset): Range => {
  const unit = preset === 'today' ? 'day' : preset === 'custom' ? 'month' : preset;
  return { start: dayjs().startOf(unit), end: dayjs().endOf(unit) };
};

/** Bucket size for the time series so the chart has a readable number of bars. */
const granularityFor = ({ start, end }: Range) => {
  const days = end.diff(start, 'day', true);
  if (days <= 1.01) return { key: 'hour', format: 'HH:mm' };
  if (days <= 62) return { key: 'day', format: 'D MMM' };
  return { key: 'month', format: 'MMM YYYY' };
};

const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const params = ({ start, end }: Range, location: string) => ({
  start_date: start.utc().format(),
  end_date: end.utc().format(),
  ...(location !== 'all' && { location }),
});

const change = (current: number, past: number) => (past > 0 ? (current - past) / past : current > 0 ? null : 0);

const baseChart = {
  fontFamily: 'Inter, sans-serif',
  toolbar: { show: false },
  zoom: { enabled: false },
  animations: { dynamicAnimation: { enabled: false } },
};
const axisLabels = { style: { colors: TRUE_COLORS.secondary } };

const Analysis = () => {
  const axios = useAxios();

  const [preset, setPreset] = React.useState<Preset>('month');
  const [range, setRange] = React.useState<Range>(presetRange('month'));
  const [location, setLocation] = React.useState('all');
  const [locations, setLocations] = React.useState<any[]>([]);

  const [loading, setLoading] = React.useState(true);
  const [summary, setSummary] = React.useState<any>(null);
  const [previous, setPrevious] = React.useState<any>(null);
  const [series, setSeries] = React.useState<any[]>([]);
  const [weekdays, setWeekdays] = React.useState<number[]>(Array(7).fill(0));
  const [byLocation, setByLocation] = React.useState<{ name: string; amount: number; count: number }[]>([]);
  const [topProducts, setTopProducts] = React.useState<{ name: string; count: number }[]>([]);
  const [topClients, setTopClients] = React.useState<{ name: string; count: number; amount: number }[]>([]);
  // How the period was paid (method, cards by brand, saldo) and the options chosen most (api/receipts/payment-summary/).
  const [payments, setPayments] = React.useState<{ methods: { payment: string; amount: number; count: number }[];
    cards: { brand: string; amount: number; count: number }[]; balance_used: number; options: { group: string; name: string; count: number }[] } | null>(null);
  // The period's gross profit, in total and per product (api/receipts/profit/; administrators only).
  const [profit, setProfit] = React.useState<Profit | null>(null);
  // Cash that came in or went out of the registers outside a sale, and why (api/movements/).
  const [movements, setMovements] = React.useState<CashMovement[]>([]);

  React.useEffect(() => {
    axios.get('api/locations/').then(({ data }) => setLocations(Array.isArray(data) ? data : [])).catch(() => {});
  }, [axios]);

  const granularity = granularityFor(range);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const span = range.end.diff(range.start, 'millisecond');
    const prevRange = { start: range.start.subtract(span + 1, 'millisecond'), end: range.start.subtract(1, 'millisecond') };
    const get = (url: string, extra: object = {}, r: Range = range) =>
      axios.get(url, { params: { ...params(r, location), ...extra } }).then(res => res.data).catch(() => null);

    (async () => {
      const [cur, prev, ts, wd, products, clients, paid, earned, cash] = await Promise.all([
        get('api/receipts/summary/'),
        get('api/receipts/summary/', {}, prevRange),
        get('api/receipts/summary/', { granularity: granularity.key, time_series: true }),
        get('api/receipts/summary/', { granularity: 'weekday' }),
        get('api/receipts/product-summary/'),
        get('api/receipts/client-summary/'),
        get('api/receipts/payment-summary/'),
        get('api/receipts/profit/'),
        get('api/movements/'),
      ]);
      // Per-branch totals: the summary endpoint filters by one location at a time.
      const perLocation = await Promise.all(
        locations.map(async (loc: any) => {
          const data = await axios
            .get('api/receipts/summary/', { params: params(range, String(loc.id)) })
            .then(res => res.data?.[0])
            .catch(() => null);
          return { name: loc.name, amount: Number(data?.total_amount) || 0, count: Number(data?.total_count) || 0 };
        }),
      );
      if (cancelled) return;

      setSummary(cur?.[0] ?? null);
      setPrevious(prev?.[0] ?? null);
      setSeries(Array.isArray(ts) ? ts : []);

      const wdTotals = Array(7).fill(0);
      (Array.isArray(wd) ? wd : []).forEach((row: any) => {
        const idx = Number(row.date) - 1; // Django ExtractWeekDay: 1 = Sunday
        if (idx >= 0 && idx < 7) wdTotals[idx] += Number(row.total_amount) || 0;
      });
      setWeekdays(wdTotals);

      const productTotals = new Map<string, number>();
      (Array.isArray(products) ? products : []).forEach((row: any) => {
        if (!row.product_name) return;
        productTotals.set(row.product_name, (productTotals.get(row.product_name) ?? 0) + (Number(row.total_count) || 0));
      });
      setTopProducts(
        Array.from(productTotals, ([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 8),
      );

      setTopClients(
        (Array.isArray(clients) ? clients : [])
          .filter((c: any) => c.client_name)
          .map((c: any) => ({
            name: [c.client_name, c.client_lastname].filter(Boolean).join(' '),
            count: Number(c.total_count) || 0,
            amount: Number(c.total_amount) || 0,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 8),
      );
      setByLocation(perLocation.sort((a, b) => b.amount - a.amount));
      setPayments(paid && Array.isArray(paid.methods) ? paid : null);
      setProfit(earned && Array.isArray(earned.products) ? earned : null);
      setMovements(Array.isArray(cash) ? cash : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [axios, range, location, locations, granularity.key]);

  const choosePreset = (value: Preset | null) => {
    if (!value) return;
    setPreset(value);
    if (value !== 'custom') setRange(presetRange(value));
  };

  const total = Number(summary?.total_amount) || 0;
  const count = Number(summary?.total_count) || 0;
  const avg = Number(summary?.avg) || 0;
  const clients = Number(summary?.total_clients) || 0;
  const prevTotal = Number(previous?.total_amount) || 0;
  const prevCount = Number(previous?.total_count) || 0;
  const prevAvg = Number(previous?.avg) || 0;
  const prevClients = Number(previous?.total_clients) || 0;
  const hasData = count > 0;

  const seriesChart = {
    options: {
      chart: { ...baseChart, type: 'bar' as const },
      colors: [CHART_COLORS[0]],
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      dataLabels: { enabled: false },
      grid: { borderColor: TRUE_COLORS.borderSolid, strokeDashArray: 4 },
      xaxis: {
        categories: series.map(row => dayjs(row.date).format(granularity.format)),
        labels: { ...axisLabels, rotate: 0, hideOverlappingLabels: true },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { ...axisLabels, formatter: (v: number) => currency(v).replace('.00', '') } },
      tooltip: { y: { formatter: (v: number) => currency(v) } },
    },
    series: [{ name: 'Ingresos', data: series.map(row => Math.round((Number(row.total_amount) || 0) * 100) / 100) }],
  };

  const weekdayChart = {
    options: {
      chart: { ...baseChart, type: 'bar' as const },
      colors: [CHART_COLORS[1]],
      plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
      dataLabels: { enabled: false },
      grid: { borderColor: TRUE_COLORS.borderSolid, strokeDashArray: 4 },
      xaxis: { categories: WEEKDAYS, labels: axisLabels, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { ...axisLabels, formatter: (v: number) => currency(v).replace('.00', '') } },
      tooltip: { y: { formatter: (v: number) => currency(v) } },
    },
    series: [{ name: 'Ingresos', data: weekdays.map(v => Math.round(v * 100) / 100) }],
  };

  const maxLocation = Math.max(1, ...byLocation.map(l => l.amount));
  const maxProduct = Math.max(1, ...topProducts.map(p => p.count));

  return (
    <PageShell
      title="Análisis"
      subtitle={`${range.start.format('D MMM YYYY')} – ${range.end.format('D MMM YYYY')}`}
      actions={
        <TextField
          select
          size="small"
          label="Sucursal"
          value={location}
          onChange={e => setLocation(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">Todas las sucursales</MenuItem>
          {locations.map(loc => <MenuItem key={loc.id} value={String(loc.id)}>{loc.name}</MenuItem>)}
        </TextField>
      }
      toolbar={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' } }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_, v) => choosePreset(v)}
            sx={{ bgcolor: 'background.paper', alignSelf: 'flex-start', maxWidth: '100%', overflowX: 'auto', '& .MuiToggleButton-root': { px: 1.75, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' } }}
          >
            {PRESETS.map(p => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
          </ToggleButtonGroup>
          {preset === 'custom' && (
            <Stack direction="row" spacing={1}>
              <DatePicker
                label="Desde"
                value={range.start}
                maxDate={range.end}
                onChange={v => v && setRange(r => ({ ...r, start: v.startOf('day') }))}
                slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
              />
              <DatePicker
                label="Hasta"
                value={range.end}
                minDate={range.start}
                onChange={v => v && setRange(r => ({ ...r, end: v.endOf('day') }))}
                slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
              />
            </Stack>
          )}
        </Stack>
      }
    >
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      <Grid container spacing={3}>
        {[
          { label: 'Ingresos', value: currency(total), delta: change(total, prevTotal), icon: <PaymentsOutlinedIcon /> },
          { label: 'Ventas', value: number(count), delta: change(count, prevCount), icon: <ReceiptLongOutlinedIcon /> },
          { label: 'Ticket promedio', value: currency(avg), delta: change(avg, prevAvg), icon: <ShoppingBasketOutlinedIcon /> },
          { label: 'Visitas de clientes', value: number(clients), delta: change(clients, prevClients), icon: <PersonOutlinedIcon /> },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
            <StatCard
              label={card.label}
              value={card.value}
              change={card.delta}
              changeLabel="vs. periodo anterior"
              icon={card.icon}
              loading={loading && !summary}
            />
          </Grid>
        ))}

        <Grid size={{ xs: 12, lg: 8 }}>
          <SectionCard title="Ingresos en el tiempo" subtitle={`Agrupado por ${granularity.key === 'hour' ? 'hora' : granularity.key === 'day' ? 'día' : 'mes'}`}>
            {hasData ? (
              <Chart type="bar" height={300} options={seriesChart.options} series={seriesChart.series} />
            ) : loading ? (
              <Skeleton variant="rounded" height={300} />
            ) : (
              <EmptyState icon={<BarChartIcon />} title="Sin ventas en este periodo" description="Prueba con un rango de fechas más amplio." />
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard title="Ventas por sucursal" subtitle="Ingresos del periodo">
            {byLocation.length === 0 ? (
              <EmptyState compact title="Sin sucursales" description="Crea una sucursal en Organización para ver este desglose." />
            ) : (
              <Stack spacing={2}>
                {byLocation.map(loc => (
                  <Box key={loc.name}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>{loc.name}</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{currency(loc.amount)}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(loc.amount / maxLocation) * 100}
                      sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(20,37,44,0.06)', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: CHART_COLORS[0] } }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 }}>
                      {number(loc.count)} {loc.count === 1 ? 'venta' : 'ventas'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {profit && hasData && (
          <Grid size={12}>
            <SectionCard title="Utilidad" subtitle="Lo que ganaste: tus ventas menos lo que te costó lo que vendiste">
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}><ProfitSummary profit={profit} /></Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>Por producto</Typography>
                  <ProfitByProduct rows={profit.products} />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        )}

        <Grid size={12}>
          <SectionCard title="Movimientos de efectivo" subtitle="Efectivo que entró o salió de la caja fuera de una venta. No cuenta en tus ingresos ni en tu utilidad.">
            {movements.length === 0 ? <CashList movements={movements} /> : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}><CashSummary movements={movements} /></Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>Cada movimiento</Typography>
                  <CashList movements={movements} />
                </Grid>
              </Grid>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard title="Por día de la semana" subtitle="¿Qué días vendes más?">
            {hasData ? (
              <Chart type="bar" height={260} options={weekdayChart.options} series={weekdayChart.series} />
            ) : (
              <EmptyState compact title="Sin datos todavía" />
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionCard title="Productos más vendidos" subtitle="Unidades vendidas">
            {topProducts.length === 0 ? (
              <EmptyState compact title="Sin productos vendidos" />
            ) : (
              <Stack spacing={1.5}>
                {topProducts.map((p, i) => (
                  <Stack key={p.name} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <Typography sx={{ width: 20, color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem' }}>{i + 1}</Typography>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{p.name}</Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, pl: 1 }}>{number(p.count, 2)}</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(p.count / maxProduct) * 100}
                        sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(20,37,44,0.06)', '& .MuiLinearProgress-bar': { bgcolor: CHART_COLORS[1] } }}
                      />
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionCard title="Clientes frecuentes" subtitle="Por número de visitas" noPadding>
            {topClients.length === 0 ? (
              <EmptyState compact title="Sin clientes registrados" description="Asocia clientes a tus ventas en TRUE POS para conocerlos mejor." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Visitas</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topClients.map(c => (
                    <TableRow key={c.name}>
                      <TableCell sx={{ fontWeight: 500 }}>{c.name}</TableCell>
                      <TableCell align="right">{number(c.count)}</TableCell>
                      <TableCell align="right">{currency(c.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionCard title="Cómo te pagan" subtitle="Por método de pago">
            {!payments || payments.methods.length === 0 ? <EmptyState compact title="Sin ventas en el periodo" /> : (
              <Stack spacing={2}>
                <Bars format={currency} rows={payments.methods.map(m => ({
                  label: PAYMENT_LABELS[m.payment] ?? (m.payment || 'Sin registrar'), value: m.amount, detail: `${number(m.count)} ${m.count === 1 ? 'venta' : 'ventas'}`,
                }))} />
                {payments.cards.length > 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="analysis-cards">
                    Tarjetas: {payments.cards.map(c => `${brandLabel(c.brand)} ${currency(c.amount)} (${number(c.count)})`).join(' · ')}
                  </Typography>
                )}
                {payments.balance_used > 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Pagado con saldo: {currency(payments.balance_used)}</Typography>
                )}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {!!payments?.options.length && (
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SectionCard title="Opciones más pedidas" subtitle="Leches, tamaños, extras…">
              <Bars format={v => number(v, 2)} rows={payments.options.map(o => ({ label: o.group ? `${o.name} · ${o.group}` : o.name, value: o.count }))} />
            </SectionCard>
          </Grid>
        )}
      </Grid>
    </PageShell>
  );
};

export default Analysis;
